import { Activity, useRef, useState } from 'react'
import { useLocalization } from '../../../../i18n'
import styles from './searchCategorySelector.module.css'
import { searchCategories, type ISearchCategory } from '../../../../constants/searchCategory'
import { useCloseOnInteractions } from '../../../../hooks/useCloseOnInteractions'
import { useToggle } from '../../../../hooks/useToggle'

interface SearchCategorySelectorProps {
  selectedCategory: ISearchCategory
  onSelect: (category: ISearchCategory) => void
}

export function SearchCategorySelector({
  selectedCategory,
  onSelect,
}: SearchCategorySelectorProps) {
  const { isOpen, toggle, close } = useToggle(false)
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const { t } = useLocalization()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  
  const wrapperRef = useCloseOnInteractions<HTMLDivElement>({
    isActive: isOpen,
    onClose: close,
  })

  const handleSelect = (category: ISearchCategory) => {
    onSelect(category)
    close()
  }

  const selectedLabel = t(selectedCategory.labelKey)

  return (
    <div className={styles.categoryWrapper} data-open={isOpen} ref={wrapperRef}>
      <button
        type="button"
        className={styles.categoryTrigger}
        onClick={toggle}
        ref={triggerRef}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls="search-category-listbox"
        aria-label={`Select search category: ${selectedLabel}`}
      >
        <span className={styles.categoryTriggerLabel}>{selectedLabel}</span>
        <span className={styles.categoryTriggerIcon} aria-hidden />
      </button>

      <Activity mode={isOpen ? 'visible' : 'hidden'}>
        <ul
          id="search-category-listbox"
          className={styles.categoryDropdown}
          role="listbox"
          tabIndex={-1}
          ref={listRef}
          aria-activedescendant={searchCategories[highlightedIndex]?.value}
        >
          {searchCategories.map((category, index) => (
            <li
              key={category.value}
              role="option"
              aria-selected={selectedCategory.value === category.value}
              className={styles.categoryOption}
              data-depth={category.depth}
              id={category.value}
              tabIndex={-1}
              onMouseEnter={() => setHighlightedIndex(index)}
              onMouseDown={event => event.preventDefault()}
              onClick={() => handleSelect(category)}
              data-highlighted={highlightedIndex === index}
            >
              <span className={styles.categoryOptionLabel}>{t(category.labelKey)}</span>
            </li>
          ))}
        </ul>
      </Activity>
    </div>
  )
}
